import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Swal from 'sweetalert2'
import apiInstance from '../../../../../Utils/api'
import BranchButton from '../shared/BranchButton'
import BranchBreadcrumb from '../shared/BranchBreadcrumb'
import BranchImageUploader from '../shared/BranchImageUploader'
import '../branch.css'

export default function BranchCreate() {
  const navigate = useNavigate()
  const [name, setName] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [code, setCode] = useState('')
  const [address, setAddress] = useState('')
  const [phones, setPhones] = useState([''])
  const [openHoursDisplay, setOpenHoursDisplay] = useState('')
  const [closedDays, setClosedDays] = useState('')
  const [lat, setLat] = useState('')
  const [lng, setLng] = useState('')
  const [images, setImages] = useState([])
  const [loading, setLoading] = useState(false)

  const handlePhoneChange = (index, value) => {
    const updated = [...phones]
    updated[index] = value
    setPhones(updated)
  }

  const addPhone = () => {
    setPhones([...phones, ''])
  }

  const removePhone = (index) => {
    if (phones.length > 1) {
      setPhones(phones.filter((_, i) => i !== index))
    }
  }

  const create = async () => {
    if (!name) {
      Swal.fire({
        title: 'Name is required!',
        text: 'Please enter branch name.',
        icon: 'error',
        showConfirmButton: false,
        timer: 2000
      })
      return
    }

    setLoading(true)
    const formData = new FormData()
    formData.append('name', name)
    if (displayName) formData.append('displayName', displayName)
    if (code) formData.append('code', code)
    if (address) formData.append('address', address)
    if (openHoursDisplay) formData.append('openHoursDisplay', openHoursDisplay)
    if (closedDays) formData.append('closedDays', closedDays)
    if (lat) formData.append('location.lat', lat)
    if (lng) formData.append('location.lng', lng)
    phones.filter(p => p.trim()).forEach(p => formData.append('phones[]', p))
    images.forEach(img => formData.append('images', img))

    try {
      await apiInstance.post('branch', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      Swal.fire({
        title: 'Successful!',
        text: 'Branch created successfully!',
        icon: 'success',
        showConfirmButton: false,
        timer: 1500
      })
      navigate('/branch')
    } catch (err) {
      setLoading(false)
      Swal.fire({
        title: 'Something Wrong!',
        text: err.response?.data?.message || 'Try again, Please.',
        icon: 'warning',
        showConfirmButton: false,
        timer: 2000
      })
    }
  }

  return (
    <div className='branch-page'>
      <BranchBreadcrumb items={[
        { label: 'Branches', to: '/branch' },
        { label: 'Create Branch' }
      ]} />

      <div className='branch-page-title'>Create Branch</div>
      <div className='branch-page-subtitle'>Add a new branch location</div>

      <div className='branch-card'>
        <div className='branch-card-body'>
          <div className='branch-form-grid'>
            {/* LEFT COLUMN */}
            <div>
              <div className='branch-form-section'>
                <div className='branch-form-section-title'>General Information</div>
                <div className='branch-field'>
                  <label className='branch-field-label'>
                    Branch Name <span className='required'>*</span>
                  </label>
                  <input
                    type='text'
                    className='branch-input'
                    placeholder='e.g. Cherry-K Downtown'
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                  />
                </div>
                <div className='branch-field'>
                  <label className='branch-field-label'>Display Name</label>
                  <input
                    type='text'
                    className='branch-input'
                    placeholder='e.g. Downtown'
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                  />
                </div>
                <div className='branch-field'>
                  <label className='branch-field-label'>Branch Code</label>
                  <input
                    type='text'
                    className='branch-input'
                    placeholder='e.g. CK-DT-001'
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                </div>
                <div className='branch-field'>
                  <label className='branch-field-label'>Phone Numbers</label>
                  {phones.map((phone, index) => (
                    <div key={index} className='branch-phone-group'>
                      <input
                        type='text'
                        className='branch-input'
                        placeholder='Phone number'
                        value={phone}
                        onChange={(e) => handlePhoneChange(index, e.target.value)}
                      />
                      {phones.length > 1 && (
                        <button
                          type='button'
                          className='branch-phone-remove'
                          onClick={() => removePhone(index)}
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  ))}
                  <BranchButton variant='outline' className='branch-phone-add' onClick={addPhone}>
                    + Add Phone
                  </BranchButton>
                </div>
              </div>

              <div className='branch-form-section'>
                <div className='branch-form-section-title'>Operating Hours</div>
                <div className='branch-field'>
                  <label className='branch-field-label'>Opening Hours Display</label>
                  <input
                    type='text'
                    className='branch-input'
                    placeholder='e.g. Mon–Fri 9:00 AM – 6:00 PM'
                    value={openHoursDisplay}
                    onChange={(e) => setOpenHoursDisplay(e.target.value)}
                  />
                </div>
                <div className='branch-field'>
                  <label className='branch-field-label'>Closed Days</label>
                  <input
                    type='text'
                    className='branch-input'
                    placeholder='e.g. Sunday, Public Holidays'
                    value={closedDays}
                    onChange={(e) => setClosedDays(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN */}
            <div>
              <div className='branch-form-section'>
                <div className='branch-form-section-title'>Location &amp; Assets</div>
                <div className='branch-field'>
                  <label className='branch-field-label'>Address</label>
                  <textarea
                    className='branch-input'
                    placeholder='Full branch address...'
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    rows={5}
                  />
                </div>
                <div className='branch-field-group'>
                  <div className='branch-field' style={{ flex: 1 }}>
                    <label className='branch-field-label'>Latitude</label>
                    <input
                      type='number'
                      step='any'
                      className='branch-input'
                      placeholder='e.g. 16.8261'
                      value={lat}
                      onChange={(e) => setLat(e.target.value)}
                    />
                  </div>
                  <div className='branch-field' style={{ flex: 1 }}>
                    <label className='branch-field-label'>Longitude</label>
                    <input
                      type='number'
                      step='any'
                      className='branch-input'
                      placeholder='e.g. 96.1184'
                      value={lng}
                      onChange={(e) => setLng(e.target.value)}
                    />
                  </div>
                </div>
                <div className='branch-field'>
                  <label className='branch-field-label'>Branch Images</label>
                  <BranchImageUploader
                    files={images}
                    onAdd={(newFiles) => setImages(prev => [...prev, ...newFiles])}
                    onRemove={(idx) => setImages(prev => prev.filter((_, i) => i !== idx))}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className='branch-form-footer'>
            <BranchButton variant='outline' to='/branch'>Cancel</BranchButton>
            <BranchButton onClick={create} loading={loading}>Save Changes</BranchButton>
          </div>
        </div>
      </div>
    </div>
  )
}
